import React, { useContext, useState } from "react";
import { Button } from "react-bootstrap";
import { StepContext } from "../../Context/StepContext";
import { useAuth } from "../../Context/AuthContext";
import AlertDialog from "../../Components/AlertDialog";
import { useNavigate } from "react-router-dom";

const AddToCartButton = ({ product, selectedPlan }) => {
  const { addItemToCart, cart } = useContext(StepContext);
  const { currentUser } = useAuth();
  const [isAdding, setIsAdding] = useState(false);
  const navigate = useNavigate();

  // Check if current item with selected plan is already in cart
  const isInCart = selectedPlan && cart.some(item => {
    const productIdentifier = product.productCode || product.id;
    const itemIdentifier = item.productCode || item.id;
    return itemIdentifier === productIdentifier && 
           item.activePlan?.id === selectedPlan.id;
  });

  const handleAddToCart = async () => {
    if (isAdding) return; // Prevent multiple clicks
    
    // Check if plan is selected
    if (!selectedPlan) {
      AlertDialog(
        "Selection Required",
        "Please select a plan before adding to cart",
        "warning",
        1500,
        false,
        false,
        "",
        "",
        null,
        null
      );
      return;
    }

    // Check if user is logged in
    if (!currentUser) {
      AlertDialog(
        "Authentication Required",
        "Please login to add items to your cart",
        "warning",
        2000,
        true,
        true,
        "Login",
        "Cancel",
        () => navigate("/Sign_in"),
        null
      );
      return;
    }

    // Check if item already exists in cart
    const productIdentifier = product.productCode || product.id || `MS-${Date.now()}`;
    const existingItem = cart.find(item => {
      // Check by product code/ID and plan combination
      const itemIdentifier = item.productCode || item.id;
      return itemIdentifier === productIdentifier && 
             item.activePlan?.id === selectedPlan.id;
    });

    if (existingItem) {
      AlertDialog(
        "Already in Cart",
        `This service with ${selectedPlan.plan_name} plan is already in your cart`,
        "info",
        2000,
        false,
        false,
        "",
        "",
        null,
        null
      );
      return;
    }

    setIsAdding(true);

    // Set a timeout to reset the adding state in case something goes wrong
    const timeoutId = setTimeout(() => {
      setIsAdding(false);
    }, 10000); // 10 second timeout

    // Create a properly structured cart item that matches CartShowcase expectations
    const cartItem = {
      // Core identification - ENSURE THIS IS A STRING
      id: `cart_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      
      // Product information
      headTitle: String(product.headTitle || product.modelName || "Motor Service"),
      modelName: String(product.modelName || "Professional Service"),
      productDescription: String(product.productDescription || "Complete motor service package"),
      productCode: String(product.productCode || `MS-${Math.random().toString(36).substr(2, 9).toUpperCase()}`),
      
      // Service information
      serviceType: String(product.serviceType || "Standard Service"),
      carType: String(product.carType || "Car"),
      engineCapacity: String(product.engineCapacity || "1.0L - 3.0L"),
      
      // Pricing and plans - ENSURE IDs ARE STRINGS
      prices: product.prices ? product.prices.map(price => ({
        ...price,
        id: String(price.id),
        price: String(price.price)
      })) : [{
        ...selectedPlan,
        id: String(selectedPlan.id),
        price: String(selectedPlan.price)
      }],
      activePlan: {
        ...selectedPlan,
        id: String(selectedPlan.id),
        price: String(selectedPlan.price),
        plan_name: String(selectedPlan.plan_name || ""),
        plan_duration: String(selectedPlan.plan_duration || "")
      },
      
      // Vehicle information (defaults - will be filled in checkout)
      carNumber: "",
      make: "",
      model: String(product.modelName || ""),
      regDate: "",
      motExpDate: "",
      currentKm: "",
      vinNumber: "",
      vehicleAccident: "",
      
      // Booking information
      bookingDate: new Date().toISOString().split('T')[0], // Today's date
      postcode: "",
      
      // Customer information (will be populated from forms)
      fname: "",
      lname: "",
      name: "",
      email: "",
      phone: "",
      phoneNumber: "",
      
      // Billing information
      address: "",
      address2: "",
      country: "",
      state: "",
      zip: "",
      
      // Media
      images: product.images || [],
      videos: product.videos || [],
      
      // Additional tracking fields
      userId: currentUser ? String(currentUser.uid) : null,
      dateAdded: new Date().toISOString()
    };

    console.log("Cart item being added:", cartItem); // Debug log

    try {
      // Add to cart and get result
      const success = await addItemToCart(cartItem);

      // Clear the timeout since we completed the operation
      clearTimeout(timeoutId);

      // Check if item was actually added by looking at cart state
      const itemExists = cart.some(item => 
        item.id === cartItem.id || 
        (item.productCode === cartItem.productCode && item.activePlan?.id === cartItem.activePlan?.id)
      );

      setIsAdding(false);

      // Show success if either the function returned true OR the item exists in cart
      if (success || itemExists) {
        AlertDialog(
          "Added to Cart",
          `${cartItem.headTitle} with ${selectedPlan.plan_name} plan has been added to your cart`,
          "success",
          2000,
          false,
          false,
          "",
          "",
          null,
          null
        );
      } else {
        AlertDialog(
          "Error",
          "Failed to add item to cart. Please try again.",
          "error",
          2000,
          false,
          false,
          "",
          "",
          null,
          null
        );
      }
    } catch (error) {
      console.error("Error adding to cart:", error);
      
      // Clear the timeout
      clearTimeout(timeoutId);
      setIsAdding(false);
      
      // Even if there's an error, check if item was added
      const itemExists = cart.some(item => 
        item.productCode === cartItem.productCode && item.activePlan?.id === cartItem.activePlan?.id
      );
      
      if (itemExists) {
        AlertDialog(
          "Added to Cart",
          `${cartItem.headTitle} with ${selectedPlan.plan_name} plan has been added to your cart`,
          "success",
          2000,
          false,
          false,
          "",
          "",
          null,
          null
        );
      } else {
        AlertDialog(
          "Error",
          "An error occurred while adding to cart. Please try again.",
          "error",
          2000,
          false,
          false,
          "",
          "",
          null,
          null
        );
      }
    }
  };

  return (
    <Button 
      onClick={handleAddToCart} 
      className={`btn shadow-0 me-2 ${isInCart ? 'btn-secondary' : 'btn-success'}`}
      disabled={isAdding || isInCart}
    >
      <i className={`me-1 fa ${isInCart ? 'fa-check' : 'fa-shopping-cart'}`} /> 
      {isAdding ? "Adding..." : isInCart ? "In Cart" : "Add to Cart"}
    </Button>
  );
};

export default AddToCartButton;